let cache = {};
import { useDevicePixelRatio } from "@vueuse/core";

export function useMagicImage() {
    const cfg = useRuntimeConfig();
    let provider = cfg.public?.magicImage?.provider || 'cloudflare';
    let zone = cfg.public?.magicImage?.zone || 'https://aiiq.pl';
    let step = cfg.public?.magicImage?.step || 128;
    let baseUrl = (cfg.public?.magicImage?.baseUrl || zone).replace(/\/+$/, '');


    function cacheData() {
        if (JSON.stringify(cache) === JSON.stringify({})) {
            const { pixelRatio } = useDevicePixelRatio();
            cache = {
                pixelRatio: pixelRatio.value
            };
        }
    }


    function toAbs(src) {
        if (/^https?:\/\//i.test(src)) {
            return src;
        }
        if (src.startsWith('/')) {
            src = src.substring(1);
        }
        if (baseUrl.endsWith('/')) {
            baseUrl = baseUrl.slice(0, -1);
        }

        return `${baseUrl}/${src}`;
    }



    const builders = {
        cloudflare(src, opts = {}) {
            // if src has / at start, remove it
            src = toAbs(src);

            const p = []
            if (opts.width) p.push(`width=${opts.width}`)
            if (opts.height) p.push(`height=${opts.height}`)
            if (opts.dpr) p.push(`dpr=${opts.dpr}`)
            if (opts.blur) p.push(`blur=${opts.blur}`)
            p.push(`quality=${opts.quality ?? 'auto'}`)
            p.push(`format=${opts.format ?? 'auto'}`)
            p.push(`fit=${opts.fit ?? 'contain'}`)
            return `${zone}/cdn-cgi/image/${p.join(',')}/${src}`
        },
        // passthrough(src) {
        //     return toAbs(src) // ignore opts, just return the file
        // }
    }
    function magicImageUrl(src, opts = {}) {
        // Return original in dev
        if (import.meta.dev) {
            return src;
        }


        return builders['cloudflare'](src, opts)
    }


    const targets = shallowRef([]);
    const currentObserverStop = ref(null);

    function init() {
        cacheData();

        const els = document.querySelectorAll('[data-magic-img-src]')
        els.forEach(el => {
            if (!el.isConnected) return

            targets.value.push(el)
        })

        const { stop } = useIntersectionObserver(
            targets,
            (entries, observer) => {
                for (const entry of entries) {
                    // unobserve if image loaded
                    const el = entry.target;
                    if (el.dataset.imgLoaded) {
                        observer.unobserve(el);
                    }
                    if (entry.isIntersecting) {
                        loadImage(el);
                    }
                }

            },
            { root: null, rootMargin: "300px", threshold: 0 }
        );

        currentObserverStop.value = stop;

        // Optional in future
        // loadImagesWhenCalm(stop);


    }

    function loadImage(element) {

        element.loading = 'eager'; // change loading of img from lazy to eager
        element.dataset.imgLoaded = true; // This is used to unobserve the element, for optimization purposes

        // Get width of element
        const width = element.clientWidth || element.parentElement?.clientWidth || 0;


        const dpr = Math.min(3, Math.max(1, Math.round(cache.pixelRatio || 1)));

        const snapped = Math.ceil(Math.round(width) / step) * step;

        element.src = magicImageUrl(element.dataset.magicImgSrc, { width: snapped, dpr: dpr, fit: element.dataset.magicImgFit || 'cover' });
    }



    function refresh() {
        // Cleanup and call init
        targets.value = [];
        if (currentObserverStop.value) {
            currentObserverStop.value();
        }
        init();
    }


    async function loadImagesWhenCalm(stopObserving) {

        // whenCalm doesn't exist at all, its some AI nonsense
        // need to implement our own version if needed
        await whenCalm(); // wait until Lighthouse stops judging 
        for (const el of targets.value) {
            if (el.dataset.imgLoaded) { continue; }
            loadImage(el);
        }

        stopObserving();
    }




    return { magicImageUrl, init, refresh }
}
